import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AssessmentOutlined,
  FileDownloadOutlined,
  TrendingUpOutlined,
  MonetizationOnOutlined,
  LocalShippingOutlined,
  BusinessOutlined,
  StorefrontOutlined,
  TwoWheelerOutlined,
  ReceiptLongOutlined,
  AccountBalanceWalletOutlined,
  PaymentsOutlined,
  AccountBalanceOutlined,
  ArrowUpwardOutlined,
  ArrowDownwardOutlined,
  PriceCheckOutlined,
  RequestQuoteOutlined,
  AccountTreeOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import { reportsService, couriersService, restaurantsService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';
import { getApiErrorMessage } from '../utils/errorHandler';

const Reports = () => {
  const { t, isRtl, dir } = useLanguage();
  const [activeTab, setActiveTab] = useState(0); // 0: Company, 1: Profitability, 2: Restaurant, 3: Courier
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Entities for statements
  const [restaurants, setRestaurants] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [selectedCourierId, setSelectedCourierId] = useState('');

  // Date Filter
  const [dateFilter, setDateFilter] = useState({
    fromDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
  });

  // Report Data
  const [reportData, setReportData] = useState([]);
  const [rawReportObject, setRawReportObject] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Initial Load of Couriers and Restaurants for statement dropdowns
  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const [restRes, couriersRes] = await Promise.allSettled([
          restaurantsService.getRestaurants(),
          couriersService.getCouriers(),
        ]);

        if (restRes.status === 'fulfilled' && restRes.value) {
          const raw = Array.isArray(restRes.value) ? restRes.value : (restRes.value?.items || restRes.value?.data || []);
          const norm = raw.map((r) => ({
            id: r.id || r.restaurantId || r.Id,
            name: r.restaurantNameAr || r.name || r.restaurantName || `مطعم #${r.id || r.restaurantId}`,
          }));
          setRestaurants(norm);
          if (norm.length > 0 && !selectedRestaurantId) {
            setSelectedRestaurantId(norm[0].id);
          }
        }

        if (couriersRes.status === 'fulfilled' && couriersRes.value) {
          const raw = Array.isArray(couriersRes.value) ? couriersRes.value : (couriersRes.value?.items || couriersRes.value?.data || []);
          const norm = raw.map((c) => {
            const cId = c.courierId ?? c.id ?? c.CourierId ?? c.Id;
            const cName = c.fullNameAr || c.fullNameEn || c.fullName || c.name || `كابتن #${cId}`;
            const cPhone = c.mobile || c.phone || '';
            return {
              id: cId,
              name: cName,
              phone: cPhone,
            };
          });
          setCouriers(norm);
          if (norm.length > 0 && !selectedCourierId) {
            setSelectedCourierId(norm[0].id);
          }
        }
      } catch (err) {
        console.warn('Failed to load entity lists for reports:', err);
      }
    };

    fetchEntities();
  }, []);

  // Fetch Report based on activeTab
  const fetchReport = async () => {
    setLoading(true);
    setReportData([]);
    setRawReportObject(null);

    const params = {
      fromDate: dateFilter.fromDate,
      toDate: dateFilter.toDate,
    };

    try {
      let res;
      if (activeTab === 0) {
        // Company Report
        res = await reportsService.getCompanyReport(params);
      } else if (activeTab === 1) {
        // Profitability Report
        res = await reportsService.getProfitability(params);
      } else if (activeTab === 2) {
        // Restaurant Statement
        if (!selectedRestaurantId) {
          setLoading(false);
          return;
        }
        res = await reportsService.getRestaurantStatement(selectedRestaurantId, params);
      } else if (activeTab === 3) {
        // Courier Statement
        if (!selectedCourierId) {
          setLoading(false);
          return;
        }
        res = await reportsService.getCourierStatement(selectedCourierId, params);
      }

      if (res) {
        if (Array.isArray(res)) {
          setReportData(res);
        } else if (res.items && Array.isArray(res.items)) {
          setReportData(res.items);
          setRawReportObject(res);
        } else if (res.data && Array.isArray(res.data)) {
          setReportData(res.data);
          setRawReportObject(res);
        } else if (res.statements && Array.isArray(res.statements)) {
          setReportData(res.statements);
          setRawReportObject(res);
        } else {
          setRawReportObject(res);
          setReportData([res]);
        }
      }
    } catch (err) {
      console.warn('Report fetch error:', err);
      setToast({
        open: true,
        message: getApiErrorMessage(err, isRtl ? 'تعذر جلب بيانات التقرير' : 'Failed to fetch report data'),
        severity: 'error',
      });
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Trigger report fetch when activeTab or selected entity changes
  useEffect(() => {
    fetchReport();
  }, [activeTab, selectedRestaurantId, selectedCourierId]);

  // Helper to trigger file download
  const downloadBlob = (blobData, defaultFileName) => {
    const blob = new Blob([blobData], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', defaultFileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Handle CSV Export
  const handleExportCsv = async () => {
    setExporting(true);
    const params = {
      fromDate: dateFilter.fromDate,
      toDate: dateFilter.toDate,
    };

    try {
      let data;
      let filename = `report_${new Date().toISOString().split('T')[0]}.csv`;

      if (activeTab === 0) {
        data = await reportsService.exportCompanyReportCsv(params);
        filename = `company_report_${dateFilter.fromDate}_${dateFilter.toDate}.csv`;
      } else if (activeTab === 1) {
        data = await reportsService.exportProfitabilityCsv(params);
        filename = `profitability_report_${dateFilter.fromDate}_${dateFilter.toDate}.csv`;
      } else if (activeTab === 2) {
        if (!selectedRestaurantId) {
          setToast({ open: true, message: isRtl ? 'يرجى اختيار المطعم أولاً' : 'Please select restaurant', severity: 'warning' });
          setExporting(false);
          return;
        }
        data = await reportsService.exportRestaurantStatementCsv(selectedRestaurantId, params);
        filename = `restaurant_${selectedRestaurantId}_statement.csv`;
      } else if (activeTab === 3) {
        if (!selectedCourierId) {
          setToast({ open: true, message: isRtl ? 'يرجى اختيار الكابتن أولاً' : 'Please select courier', severity: 'warning' });
          setExporting(false);
          return;
        }
        data = await reportsService.exportCourierStatementCsv(selectedCourierId, params);
        filename = `courier_${selectedCourierId}_statement.csv`;
      }

      if (data) {
        downloadBlob(data, filename);
        setToast({ open: true, message: isRtl ? 'تم تحميل ملف CSV بنجاح!' : 'CSV exported successfully!', severity: 'success' });
      }
    } catch (err) {
      setToast({
        open: true,
        message: getApiErrorMessage(err, isRtl ? 'فشل تصدير ملف CSV' : 'Failed to export CSV'),
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  // Preset Date Range buttons
  const applyDatePreset = (days) => {
    const to = new Date().toISOString().split('T')[0];
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setDateFilter({ fromDate: from, toDate: to });
  };

  // Calculate Aggregates
  const totalOrders = rawReportObject?.totalOrders ?? reportData.reduce((acc, curr) => acc + (Number(curr.totalOrders || curr.ordersCount || (curr.id ? 1 : 0))), 0);
  const totalSales = rawReportObject?.totalSales ?? reportData.reduce((acc, curr) => acc + (parseFloat(curr.totalSales || curr.totalAmount || curr.amount || 0)), 0);
  const totalDeliveryFees = rawReportObject?.totalDeliveryFees ?? reportData.reduce((acc, curr) => acc + (parseFloat(curr.deliveryFee || curr.totalDeliveryFee || 0)), 0);
  const totalCompanyProfit = rawReportObject?.companyProfit ?? rawReportObject?.netProfit ?? reportData.reduce((acc, curr) => acc + (parseFloat(curr.companyCommission || curr.netProfit || curr.profit || 0)), 0);

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('reportsPage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('reportsPage.subtitle')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          disabled={exporting}
          startIcon={exporting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <FileDownloadOutlined sx={{ fontSize: 16 }} />}
          onClick={handleExportCsv}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            py: 0.6,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
          }}
        >
          {exporting ? (isRtl ? 'جاري التصدير...' : 'Exporting...') : (t('reportsPage.exportCsv') || 'تصدير CSV')}
        </Button>
      </Box>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 2.5, p: 0.5 }} dir={dir}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: '0.84rem',
              minHeight: 44,
              py: 1,
            },
          }}
        >
          <Tab icon={<BusinessOutlined sx={{ fontSize: 18, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} />} iconPosition="start" label={t('reportsPage.tabs.company')} />
          <Tab icon={<TrendingUpOutlined sx={{ fontSize: 18, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} />} iconPosition="start" label={t('reportsPage.tabs.profitability')} />
          <Tab icon={<StorefrontOutlined sx={{ fontSize: 18, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} />} iconPosition="start" label={t('reportsPage.tabs.restaurant')} />
          <Tab icon={<TwoWheelerOutlined sx={{ fontSize: 18, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} />} iconPosition="start" label={t('reportsPage.tabs.courier')} />
        </Tabs>
      </Card>

      {/* Filters Card */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Dynamic Entity Selectors */}
            {activeTab === 2 && (
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  size="small"
                  label={t('reportsPage.selectRestaurant')}
                  fullWidth
                  value={selectedRestaurantId}
                  onChange={(e) => setSelectedRestaurantId(e.target.value)}
                >
                  {restaurants.length === 0 ? (
                    <MenuItem disabled value="">{isRtl ? 'لا توجد مطاعم مسجلة' : 'No restaurants'}</MenuItem>
                  ) : (
                    restaurants.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
            )}

            {activeTab === 3 && (
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  size="small"
                  label={t('reportsPage.selectCourier')}
                  fullWidth
                  value={selectedCourierId}
                  onChange={(e) => setSelectedCourierId(e.target.value)}
                >
                  {couriers.length === 0 ? (
                    <MenuItem disabled value="">{isRtl ? 'لا يوجد كباتن مسجلين' : 'No couriers'}</MenuItem>
                  ) : (
                    couriers.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name} {c.phone ? `(${c.phone})` : ''}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={activeTab >= 2 ? 3 : 3.5}>
              <TextField
                type="date"
                label={isRtl ? 'من تاريخ' : 'From Date'}
                size="small"
                fullWidth
                value={dateFilter.fromDate}
                onChange={(e) => setDateFilter({ ...dateFilter, fromDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={activeTab >= 2 ? 3 : 3.5}>
              <TextField
                type="date"
                label={isRtl ? 'إلى تاريخ' : 'To Date'}
                size="small"
                fullWidth
                value={dateFilter.toDate}
                onChange={(e) => setDateFilter({ ...dateFilter, toDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={activeTab >= 2 ? 2 : 2}>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<AssessmentOutlined />}
                onClick={fetchReport}
                sx={{ height: 40, borderRadius: '8px', fontWeight: 700 }}
              >
                {t('filter')}
              </Button>
            </Grid>

            <Grid item xs={12} sm={activeTab >= 2 ? 12 : 3} sx={{ display: 'flex', gap: 0.5, justifyContent: isRtl ? 'flex-start' : 'flex-end', flexWrap: 'wrap' }}>
              <Button size="small" variant="text" onClick={() => applyDatePreset(0)} sx={{ fontSize: '0.75rem' }}>{isRtl ? 'اليوم' : 'Today'}</Button>
              <Button size="small" variant="text" onClick={() => applyDatePreset(7)} sx={{ fontSize: '0.75rem' }}>{isRtl ? '7 أيام' : '7 Days'}</Button>
              <Button size="small" variant="text" onClick={() => applyDatePreset(30)} sx={{ fontSize: '0.75rem' }}>{isRtl ? '30 يوم' : '30 Days'}</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ======================================================== */}
      {/* TAB 0: COMPANY FINANCIAL STATEMENT (13 METRICS)           */}
      {/* ======================================================== */}
      {activeTab === 0 ? (
        <>
          {/* Top 4 Core Financial KPIs */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={isRtl ? 'إجمالي الإيرادات (Revenue)' : 'Total Revenue'}
                value={`${(parseFloat(rawReportObject?.revenue ?? 0)).toFixed(2)} ${t('currency')}`}
                icon={MonetizationOnOutlined}
                color="#10B981"
                subtitle={isRtl ? 'إيرادات العمليات والخدمات' : 'Operating revenue'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={isRtl ? 'المصروفات والتكاليف (Expenses)' : 'Total Expenses'}
                value={`${(parseFloat(rawReportObject?.expenses ?? 0)).toFixed(2)} ${t('currency')}`}
                icon={PaymentsOutlined}
                color="#EF4444"
                subtitle={isRtl ? 'تكاليف التشغيل والمصاريف' : 'Operating expenses'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={isRtl ? 'صافي الأرباح (Net Profit)' : 'Net Profit'}
                value={`${(parseFloat(rawReportObject?.netProfit ?? 0)).toFixed(2)} ${t('currency')}`}
                icon={TrendingUpOutlined}
                color="#8B5CF6"
                subtitle={isRtl ? 'صافي عائد المنظومة' : 'Net business margin'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={isRtl ? 'رصيد النقدية الختامي (Cash)' : 'Cash Closing'}
                value={`${(parseFloat(rawReportObject?.cashClosing ?? 0)).toFixed(2)} ${t('currency')}`}
                icon={AccountBalanceWalletOutlined}
                color="#3B82F6"
                subtitle={isRtl ? 'الرصيد المتاح بالخزينة' : 'Available cash balance'}
              />
            </Grid>
          </Grid>

          {/* 3 Financial Breakdown Panels */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {/* Panel 1: Cash Flow & Treasury */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AccountBalanceOutlined color="primary" sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {isRtl ? '1. حركة النقدية والخزينة' : '1. Cash Flow & Treasury'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{isRtl ? 'الرصيد الافتتاحي (أول المدة):' : 'Cash Opening:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{(parseFloat(rawReportObject?.cashOpening ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ArrowUpwardOutlined sx={{ fontSize: 14 }} /> {isRtl ? 'المقبوضات النقدية (وارد):' : 'Cash Inflow (Debit):'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>+{(parseFloat(rawReportObject?.cashDebit ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ArrowDownwardOutlined sx={{ fontSize: 14 }} /> {isRtl ? 'المدفوعات النقدية (منصرف):' : 'Cash Outflow (Credit):'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>-{(parseFloat(rawReportObject?.cashCredit ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{isRtl ? 'الرصيد الختامي (آخر المدة):' : 'Cash Closing:'}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>{(parseFloat(rawReportObject?.cashClosing ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Panel 2: Receivables & Couriers */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TwoWheelerOutlined color="warning" sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {isRtl ? '2. المستحقات والعهدة الميدانية' : '2. Receivables & Couriers'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{isRtl ? 'مستحقات على المطاعم:' : 'Restaurant Receivables:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.main' }}>{(parseFloat(rawReportObject?.restaurantReceivables ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{isRtl ? 'عهدة نقدية لدى الكباتن:' : 'Courier Custody:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{(parseFloat(rawReportObject?.courierCustody ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{isRtl ? 'أرباح مستحقة الدفع للكباتن:' : 'Courier Earnings Payable:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>{(parseFloat(rawReportObject?.courierEarningsPayable ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{isRtl ? 'سلف الكباتن القائمة:' : 'Courier Advances:'}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'error.main' }}>{(parseFloat(rawReportObject?.courierAdvances ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Panel 3: Revenue Accrual & Realization */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PriceCheckOutlined color="success" sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {isRtl ? '3. الإيرادات وكفاءة التحصيل' : '3. Revenue & Realization'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">{isRtl ? 'إجمالي الإيرادات المحتسبة:' : 'Accrued Revenue:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{(parseFloat(rawReportObject?.revenue ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="success.main">{isRtl ? 'الإيرادات المحصلة نقداً:' : 'Collected Cash Revenue:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{(parseFloat(rawReportObject?.collectedRevenueCash ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="warning.main">{isRtl ? 'إيرادات مستحقة غير محصلة:' : 'Accrued Less Collected:'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>{(parseFloat(rawReportObject?.accruedLessCollected ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{isRtl ? 'صافي الربح النهائي:' : 'Net Profit Margin:'}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'secondary.main' }}>{(parseFloat(rawReportObject?.netProfit ?? 0)).toFixed(2)} {t('currency')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Detailed Financial Ledger Table */}
          <Card sx={{ borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: 'primary.main' }}>
                {isRtl ? 'جدول البيان المالي الشامل للشركة' : 'Company Comprehensive Financial Statement Table'}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>#</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'البند المحاسبي' : 'Financial Item'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'التصنيف' : 'Classification'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'القيمة' : 'Amount'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { key: 'revenue', nameAr: 'إجمالي الإيرادات', nameEn: 'Total Revenue', catAr: 'إيرادات تشغيل', catEn: 'Revenue', color: 'success.main', isBold: true },
                      { key: 'expenses', nameAr: 'المصروفات والتكاليف', nameEn: 'Expenses', catAr: 'تكاليف تشغيل', catEn: 'Expenses', color: 'error.main', isBold: true },
                      { key: 'netProfit', nameAr: 'صافي الربح', nameEn: 'Net Profit', catAr: 'الأرباح الصافية', catEn: 'Profit', color: 'secondary.main', isBold: true },
                      { key: 'cashOpening', nameAr: 'الرصيد الافتتاحي للنقدية', nameEn: 'Cash Opening', catAr: 'أصول نقدية', catEn: 'Assets', color: 'text.primary' },
                      { key: 'cashDebit', nameAr: 'المقبوضات النقدية (مدين)', nameEn: 'Cash Debit', catAr: 'تدفقات نقدية داخلة', catEn: 'Inflow', color: 'success.main' },
                      { key: 'cashCredit', nameAr: 'المدفوعات النقدية (دائن)', nameEn: 'Cash Credit', catAr: 'تدفقات نقدية خارجة', catEn: 'Outflow', color: 'error.main' },
                      { key: 'cashClosing', nameAr: 'الرصيد الختامي للنقدية', nameEn: 'Cash Closing', catAr: 'أصول نقدية', catEn: 'Assets', color: 'primary.main', isBold: true },
                      { key: 'restaurantReceivables', nameAr: 'مستحقات على المطاعم', nameEn: 'Restaurant Receivables', catAr: 'مدينون وذمم', catEn: 'Receivables', color: 'text.primary' },
                      { key: 'courierCustody', nameAr: 'عهدة الكباتن المحصلة', nameEn: 'Courier Custody', catAr: 'عهد نقدية', catEn: 'Custody', color: 'text.primary' },
                      { key: 'courierEarningsPayable', nameAr: 'مستحقات أرباح الكباتن', nameEn: 'Courier Earnings Payable', catAr: 'التزامات ومستحقات', catEn: 'Payables', color: 'warning.main' },
                      { key: 'courierAdvances', nameAr: 'سلف الكباتن القائمة', nameEn: 'Courier Advances', catAr: 'سلف وعهد مؤقتة', catEn: 'Advances', color: 'error.main' },
                      { key: 'collectedRevenueCash', nameAr: 'الإيرادات المحصلة نقداً', nameEn: 'Collected Cash Revenue', catAr: 'إيرادات محصلة', catEn: 'Realized Revenue', color: 'success.main' },
                      { key: 'accruedLessCollected', nameAr: 'الإيرادات المستحقة غير المحصلة', nameEn: 'Accrued Less Collected', catAr: 'إيرادات مؤجلة', catEn: 'Accruals', color: 'warning.main' },
                    ].map((row, i) => {
                      const val = parseFloat(rawReportObject?.[row.key] ?? 0);
                      return (
                        <TableRow key={row.key} hover sx={{ bgcolor: row.isBold ? 'action.hover' : 'inherit' }}>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700, color: 'text.secondary' }}>{i + 1}</TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: row.isBold ? 800 : 600 }}>
                            {isRtl ? row.nameAr : row.nameEn}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Chip label={isRtl ? row.catAr : row.catEn} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 800, color: row.color, fontSize: '0.84rem' }}>
                            {val.toFixed(2)} {t('currency')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : (
        /* Other Tabs: Profitability / Statements */
        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <LoadingState />
            ) : reportData.length === 0 ? (
              <EmptyState
                message={t('noData')}
                subtitle={isRtl ? 'لا توجد بيانات متاحة لهذا التقرير في النطاق الزمني المحدد' : 'No records found for the selected period'}
              />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 130 }}>{isRtl ? 'التاريخ' : 'Date'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 160 }}>{isRtl ? 'البيان / المعاملة' : 'Description'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 150 }}>{isRtl ? 'الطرف المعني' : 'Entity'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{isRtl ? 'قيمة المبيعات' : 'Sales'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{t('ordersPage.deliveryFee')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{isRtl ? 'صافي الربح / العمولة' : 'Profit / Commission'}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 100 }}>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                            {item.date || item.businessDate || item.createdAt || dateFilter.fromDate}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            {item.description || item.statementType || item.reference || (isRtl ? 'كشف مالي' : 'Statement Record')}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                            {item.restaurantName || item.courierName || item.partyName || (activeTab === 2 ? (restaurants.find(r => r.id === selectedRestaurantId)?.name) : activeTab === 3 ? (couriers.find(c => c.id === selectedCourierId)?.name) : (isRtl ? 'الشركة العامة' : 'Company'))}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {(parseFloat(item.totalSales || item.totalAmount || item.amount || 0)).toFixed(2)} {t('currency')}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {(parseFloat(item.deliveryFee || item.totalDeliveryFee || 0)).toFixed(2)} {t('currency')}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
                            {(parseFloat(item.companyCommission || item.netProfit || item.profit || item.courierEarnings || 0)).toFixed(2)} {t('currency')}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                          <Chip
                            label={item.status || (isRtl ? 'معتمد ومرحل' : 'Settled')}
                            color={item.status === 'Cancelled' ? 'error' : 'success'}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Reports;
